import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation, Trans } from 'react-i18next'
import { createDebug } from '../utils/debugLog'
import { useLocaleFormat } from '../i18n/useLocaleFormat'
import { useAuth } from '../contexts/AuthContext'
import { getAccountStatus, restoreAccount, AccountStatus } from '../api/auth'
import { supabase } from '../utils/supabase'
import { APP_NAME } from '../constants/brand'

const log = createDebug('dw:account-self-service:restore-page')

const RESTORE_DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
}

export default function AccountRestorePage() {
  const navigate = useNavigate()
  const { t } = useTranslation('auth')
  const { formatDate } = useLocaleFormat()
  // Preserve the legacy fallback: on an unparseable date, show the raw value
  // rather than a blank. formatDate() returns '' for invalid input, so we
  // detect that and fall back to the original ISO string.
  const formatPurgeDate = (iso: string | null): string => {
    if (!iso) return ''
    const formatted = formatDate(iso, RESTORE_DATE_OPTIONS)
    return formatted || iso
  }
  const { isAuthenticated, isLoading } = useAuth()
  const [status, setStatus] = useState<AccountStatus | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const data = await getAccountStatus()
        if (cancelled) return
        log('status fetched soft_deleted=%s days_remaining=%s', data.soft_deleted, data.days_remaining)
        setStatus(data)
        if (!data.soft_deleted) {
          navigate('/dashboard')
          return
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : t('failedToLoadAccountStatus')
        log('status fetch error %s', message)
        setError(message)
      } finally {
        if (!cancelled) setLoaded(true)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isLoading, isAuthenticated, navigate, t])

  const handleRestore = async () => {
    if (submitting) return
    setSubmitting(true)
    setError(null)
    log('restore submit')
    try {
      await restoreAccount()
      log('restore success — hard reload to /')
      // Hard reload so every page re-fetches its data with the now-active account.
      window.location.href = '/'
    } catch (err) {
      const message = err instanceof Error ? err.message : t('failedToRestoreAccount')
      log('restore error %s', message)
      setError(message)
      setSubmitting(false)
    }
  }

  const handleSignOut = async () => {
    log('signout from restore page')
    await supabase.auth.signOut()
    navigate('/')
  }

  if (isLoading || !loaded) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-sm text-muted-foreground">{t('loadingStatus')}</div>
      </div>
    )
  }

  if (!status?.soft_deleted) {
    return null
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background text-foreground">
      <div
        className="w-full max-w-md flex flex-col gap-4 p-6 rounded-lg border border-border bg-surface-1"
        data-testid="account-restore-page"
      >
        <h1 className="text-lg font-semibold">{t('accountScheduledForDeletion')}</h1>
        <p className="text-sm text-muted-foreground">
          <Trans
            i18nKey="scheduledDeletionMessage"
            t={t}
            values={{
              date: formatPurgeDate(status.scheduled_purge_at),
              daysInfo: status.days_remaining !== null && status.days_remaining > 0
                ? ` ${t('daysRemaining', { count: status.days_remaining })}`
                : '',
            }}
            components={{
              dateSpan: <span className="text-foreground font-medium" data-testid="restore-purge-date" />,
            }}
          />
        </p>
        <p className="text-sm text-muted-foreground">
          {t('restoreAccountMessage', { appName: APP_NAME })}
        </p>
        {error && (
          <div className="text-xs font-medium text-red-400" data-testid="restore-error">
            {error}
          </div>
        )}
        <button
          type="button"
          data-testid="restore-button"
          onClick={handleRestore}
          disabled={submitting}
          className="w-full px-4 py-2.5 rounded-md text-sm font-medium bg-primary hover:bg-primary-dark text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
        >
          {submitting ? t('restoring') : t('restoreMyAccount')}
        </button>
        <button
          type="button"
          data-testid="restore-signout"
          onClick={handleSignOut}
          className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
        >
          {t('signOut')}
        </button>
      </div>
    </div>
  )
}
